'use client';

import { Box } from '@terra/ui-styled-system';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import type { HStack } from '@terra/ui-styled-system';
import type { TodoItem, TodosMessage } from '@terra/shared-dto-chat-types';
import {
  CancelCircleIcon,
  CircleIcon,
  Loading03Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
  type TraceStatus,
} from './trace';
import { splitTodoQueue } from './split-todo-queue';

type TodosMessageCardProps = {
  dataMessage: TodosMessage;
  maxHeight?: ComponentProps<typeof HStack>['maxHeight'];
  isChatLoading?: boolean;
};

function todoState(todo: TodoItem): TodoItem['state'] {
  if (
    todo.state === 'completed' ||
    todo.state === 'in_progress' ||
    todo.state === 'cancelled' ||
    todo.state === 'pending'
  ) {
    return todo.state;
  }

  return 'pending';
}

function todoPresentation(todo: TodoItem): {
  status?: TraceStatus;
  icon?: ReactNode;
} {
  const state = todoState(todo);

  if (state === 'in_progress') {
    return { status: 'running', icon: <Loading03Icon size={12} /> };
  }

  if (state === 'completed') {
    return { icon: <Tick01Icon size={12} /> };
  }

  if (state === 'cancelled') {
    return { icon: <CancelCircleIcon size={12} /> };
  }

  return { status: 'pending', icon: <CircleIcon size={12} /> };
}

export function TodosMessageCard({
  dataMessage,
  maxHeight,
  isChatLoading,
}: TodosMessageCardProps) {
  const t = useTranslations('designSystem.chat');
  const [open, setOpen] = useState(false);
  const todos = (dataMessage.todos ?? []).map((todo) => ({
    ...todo,
    state: todoState(todo),
  }));

  if (todos.length === 0) {
    return null;
  }

  const queue = splitTodoQueue(todos);

  return (
    <TraceCollapsible
      open={open}
      onOpenChange={({ open: nextOpen }) => setOpen(nextOpen)}
    >
      <TraceCollapsibleTrigger>
        <TraceLine
          title={t('updatedTodos')}
          status={isChatLoading ? 'running' : undefined}
          canExpand
          isExpanded={open}
        />
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent open={open}>
        <Box
          maxHeight={maxHeight ?? '180px'}
          overflowY="auto"
          overflowX="hidden"
        >
          <Trace nested>
            {[...queue.remaining, ...queue.done].map((todo) => {
              const presentation = todoPresentation(todo);
              const state = todoState(todo);

              return (
                <TraceLine
                  key={todo.id}
                  title={todo.content}
                  status={presentation.status}
                  icon={presentation.icon}
                  titleStyle={
                    state === 'completed'
                      ? { textDecoration: 'line-through' }
                      : undefined
                  }
                />
              );
            })}
          </Trace>
        </Box>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}
